/* Pixel Threads — TUNER v1.0
   The green panel is a scope. Move across it to tune. A hidden frequency is
   rolled once per session. Proximity coheres the static. Inside the lock band
   stillness resolves a coded line out of the noise, in the same Bayer dither
   language as RESOLVE: attention causes the resolve. Movement scatters the
   resolve part way, never all the way, so a re-hold continues instead of
   starting over.

   Progressive enhancement only. Without JS, without canvas, or under reduced
   motion the panel is exactly the panel that shipped and the line is plain
   text. Every failure path removes the canvas and leaves the DOM as authored.

   Mount:  [data-pt-tune]                  the panel
           [data-pt-tune-line]             real text, the resolved payoff
           [data-pt-tune-hint]             the affordance, hidden once locked
           script[data-pt-tune-config]     JSON: { lines: [...], label, hint } */
(function () {
  'use strict';

  var root = document.querySelector('[data-pt-tune]');
  if (!root) return;

  var lineEl = root.querySelector('[data-pt-tune-line]');
  var hintEl = root.querySelector('[data-pt-tune-hint]');
  if (!lineEl) return;

  /* ---- config ----------------------------------------------------------- */

  var cfg = {};
  try {
    var cfgEl = root.querySelector('script[data-pt-tune-config]');
    if (cfgEl) cfg = JSON.parse(cfgEl.textContent) || {};
  } catch (e) {}

  var LINES = [];
  if (cfg.lines && cfg.lines.length) {
    for (var li = 0; li < cfg.lines.length; li++) {
      var raw = String(cfg.lines[li]).replace(/\s+/g, ' ').trim();
      if (raw) LINES.push(raw.toUpperCase());
    }
  }
  if (!LINES.length) LINES = ['YOU WERE ALWAYS THE SIGNAL.'];

  var SEEK_RANGE = 0.30;   /* how far out the static starts reacting */
  var LOCK_BAND = 0.055;   /* generous by design, and wider again on touch */
  var HOLD_SECONDS = 1.15; /* stillness needed for a full resolve */
  var STILL_GRACE = 0.08;  /* a beat before a stop counts as a hold */
  var RATCHET = 0.55;      /* a scatter never falls below this share of peak */
  var SCATTER_RATE = 0.85; /* resolve lost per second while moving */
  var STORE_KEY = 'pt-tune';

  /* ---- environment ------------------------------------------------------ */

  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  /* Everything the viewport decides is derived in one place and re-derived on
     resize. Setting these independently let LOCK_BAND and JITTER go stale after
     a rotate while MOBILE had already flipped. */
  var MOBILE, JITTER, lineYRatio = 0.5;
  function applyViewport() {
    /* A zero width means the viewport is not measurable yet (prerender, a
       hidden frame, a backgrounded tab). Treating that as mobile would put the
       resolve where a finger would be on a desktop panel, so hold the desktop
       default until a real width arrives and let the resize handler correct it. */
    var vw = window.innerWidth || document.documentElement.clientWidth || 0;
    MOBILE = vw > 0 && !!(window.matchMedia && window.matchMedia('(max-width: 780px)').matches);
    JITTER = MOBILE ? 12 : 7;      /* px of pointer noise tolerated while holding */
    LOCK_BAND = MOBILE ? 0.075 : 0.055;
    if (!MOBILE) lineYRatio = 0.5;
  }

  applyViewport();

  var state = {};
  try { state = JSON.parse(sessionStorage.getItem(STORE_KEY) || '{}') || {}; } catch (e) {}

  /* the hidden frequency: rolled once per session, never within 0.16 of the
     resting caret, never against an edge where it cannot be approached. */
  var target = typeof state.f === 'number' ? state.f : null;
  if (target === null || target < 0.1 || target > 0.9) {
    do { target = 0.12 + Math.random() * 0.76; } while (Math.abs(target - 0.5) < 0.16);
  }
  var line = typeof state.l === 'string' && LINES.indexOf(state.l) > -1
    ? state.l
    : LINES[Math.floor(Math.random() * LINES.length)];
  var alreadyFound = state.done === 1;

  function persist() {
    try {
      sessionStorage.setItem(STORE_KEY, JSON.stringify({
        f: target, l: line, done: alreadyFound ? 1 : 0
      }));
    } catch (e) {}
  }
  persist();

  lineEl.textContent = line;

  /* Reduced motion, no canvas, or already solved this session: hand over the
     resolved line with no theatre and stop. The panel keeps its decorations. */
  function surrender() {
    root.classList.add('is-resolved');
    root.classList.remove('is-tuning');
    if (hintEl) hintEl.hidden = true;
    lineEl.removeAttribute('aria-hidden');
  }

  var probe = document.createElement('canvas');
  if (reduce || alreadyFound || !probe.getContext || !window.requestAnimationFrame ||
      (window.Shopify && window.Shopify.designMode)) {
    surrender();
    return;
  }

  /* ---- palette ---------------------------------------------------------- */

  var cs = window.getComputedStyle(document.documentElement);
  var INK = (cs.getPropertyValue('--ink') || '').trim() || '#080808';

  var BAYER = [
    0, 32, 8, 40, 2, 34, 10, 42,
    48, 16, 56, 24, 50, 18, 58, 26,
    12, 44, 4, 36, 14, 46, 6, 38,
    60, 28, 52, 20, 62, 30, 54, 22,
    3, 35, 11, 43, 1, 33, 9, 41,
    51, 19, 59, 27, 49, 17, 57, 25,
    15, 47, 7, 39, 13, 45, 5, 37,
    63, 31, 55, 23, 61, 29, 53, 21
  ];

  /* ---- canvas ----------------------------------------------------------- */

  var canvas = document.createElement('canvas');
  canvas.className = 'pt-tune__canvas';
  canvas.setAttribute('aria-hidden', 'true');
  var ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) { surrender(); return; }
  root.appendChild(canvas);

  /* offscreen: the payoff line rasterised once per layout, read as luminance
     and then dithered. Text is the source image, exactly as a photo is in
     RESOLVE. */
  var textCanvas = document.createElement('canvas');
  var textCtx = textCanvas.getContext('2d', { willReadFrequently: true });
  var textLum = null;

  var W = 0, H = 0, SCALE = 0.5;

  function fontStack() {
    var f = (window.getComputedStyle(root).getPropertyValue('font-family') || '').trim();
    return f || '"PT Geist Mono", "SFMono-Regular", Consolas, monospace';
  }

  function layoutText() {
    if (!W || !H) return;
    textCanvas.width = W;
    textCanvas.height = H;
    var size = Math.max(11, Math.min(26, Math.round(W * 0.052)));
    var track = size * 0.16;
    textCtx.fillStyle = '#fff';
    textCtx.fillRect(0, 0, W, H);
    textCtx.fillStyle = '#000';
    textCtx.textBaseline = 'middle';
    textCtx.font = '600 ' + size + 'px ' + fontStack();

    /* wrap on words, to about 78% of the panel */
    var maxW = W * 0.78;
    var words = line.split(' ');
    var rows = [];
    var cur = '';
    for (var i = 0; i < words.length; i++) {
      var next = cur ? cur + ' ' + words[i] : words[i];
      if (measure(next, track) > maxW && cur) { rows.push(cur); cur = words[i]; }
      else { cur = next; }
    }
    if (cur) rows.push(cur);

    var lh = size * 1.42;
    var block = rows.length * lh;
    var top = H * lineYRatio - block / 2 + lh / 2;
    for (var r = 0; r < rows.length; r++) {
      drawTracked(rows[r], (W - measure(rows[r], track)) / 2, top + r * lh, track);
    }
    try {
      textLum = textCtx.getImageData(0, 0, W, H).data;
    } catch (e) {
      textLum = null;
    }

    /* the DOM line has to land exactly where the dither left it, so the
       hand-off at lock reads as one object sharpening, not two elements */
    root.style.setProperty('--pt-tune-size', (size / SCALE) + 'px');
    root.style.setProperty('--pt-tune-y', (lineYRatio * 100) + '%');
  }

  function measure(str, track) {
    return textCtx.measureText(str).width + track * Math.max(0, str.length - 1);
  }

  function drawTracked(str, x, y, track) {
    for (var i = 0; i < str.length; i++) {
      var ch = str.charAt(i);
      textCtx.fillText(ch, x, y);
      x += textCtx.measureText(ch).width + track;
    }
  }

  function resize() {
    var r = root.getBoundingClientRect();
    var w = Math.max(8, Math.round(r.width * SCALE));
    var h = Math.max(8, Math.round(r.height * SCALE));
    if (w === W && h === H) return;
    W = w; H = h;
    canvas.width = W;
    canvas.height = H;
    layoutText();
  }

  /* ---- tuning state ----------------------------------------------------- */

  var x = 0.5;            /* caret, 0..1 across the panel */
  var hold = 0;           /* resolve, 0..1 */
  var floorHold = 0;      /* the ratchet: a scatter never drops below this */
  var holdBase = null;    /* hold at the instant the current stillness began */
  var movedAt = -1e9;     /* wall clock of the last real movement */
  var lastPointer = null; /* {cx, cy} in px, for the jitter test */
  var engaged = false;    /* pointer or keyboard has taken hold of the panel */
  var presence = 0;       /* eased 0..1: at rest the panel is the panel that shipped */
  var visible = false;
  var done = false;
  var rafId = null;
  var lastT = 0;
  var noiseSeed = 1;

  function clamp01(v) { return v < 0 ? 0 : v > 1 ? 1 : v; }

  /* deterministic per-frame noise, cheap and stable enough to look like a field */
  function rnd() {
    noiseSeed = (noiseSeed * 1664525 + 1013904223) & 0x7fffffff;
    return noiseSeed / 0x7fffffff;
  }

  function proximity() {
    var d = Math.abs(x - target);
    if (d >= SEEK_RANGE) return 0;
    return clamp01(1 - d / SEEK_RANGE);
  }

  function inBand() { return Math.abs(x - target) <= LOCK_BAND; }

  /* ---- a11y ------------------------------------------------------------- */

  root.setAttribute('role', 'slider');
  root.setAttribute('tabindex', '0');
  root.setAttribute('aria-valuemin', '0');
  root.setAttribute('aria-valuemax', '100');
  root.setAttribute('aria-label', cfg.label || 'Signal tuner. Tune with the arrow keys, then hold still to resolve the line.');
  lineEl.setAttribute('aria-hidden', 'true');

  var lastSpoken = '';
  function announce() {
    root.setAttribute('aria-valuenow', String(Math.round(x * 100)));
    var word;
    if (done) word = 'Locked. ' + line;
    else if (inBand()) word = 'Signal. Hold still.';
    else if (proximity() > 0.55) word = 'Close.';
    else if (proximity() > 0.2) word = 'Faint.';
    else word = 'Static.';
    if (word !== lastSpoken) {
      lastSpoken = word;
      root.setAttribute('aria-valuetext', word);
    }
  }
  announce();

  /* ---- input ------------------------------------------------------------ */

  function engage() {
    if (engaged || done) return;
    engaged = true;
    root.classList.add('is-tuning');
    ensureLoop();
  }

  function tuneTo(clientX, clientY) {
    var r = root.getBoundingClientRect();
    if (!r.width) return;
    var nx = clamp01((clientX - r.left) / r.width);
    var moved = lastPointer
      ? Math.abs(clientX - lastPointer.cx) + Math.abs(clientY - lastPointer.cy)
      : 999;
    if (moved > JITTER) {
      movedAt = performance.now();
      lastPointer = { cx: clientX, cy: clientY };
    }
    x = nx;
    /* on touch the resolve sits above the finger so the hand never covers it */
    if (MOBILE && r.height) {
      lineYRatio = clamp01((clientY - r.top) / r.height - 0.22);
      if (lineYRatio < 0.14) lineYRatio = 0.14;
      layoutText();
    }
    engage();
    announce();
  }

  root.addEventListener('pointermove', function (ev) {
    if (done) return;
    if (ev.pointerType === 'touch' && !ev.isPrimary) return;
    tuneTo(ev.clientX, ev.clientY);
  }, { passive: true });

  root.addEventListener('pointerdown', function (ev) {
    if (done) return;
    tuneTo(ev.clientX, ev.clientY);
  }, { passive: true });

  root.addEventListener('pointerleave', function () {
    lastPointer = null;
    movedAt = performance.now();
  }, { passive: true });

  /* keyboard: arrows tune, dwell locks. Home and End jump the ends. */
  var STEP = 0.02, FINE = 0.005;
  root.addEventListener('keydown', function (ev) {
    if (done) return;
    var step = ev.shiftKey ? FINE : STEP;
    var handled = true;
    switch (ev.key) {
      case 'ArrowRight': case 'ArrowUp': x = clamp01(x + step); break;
      case 'ArrowLeft': case 'ArrowDown': x = clamp01(x - step); break;
      case 'Home': x = 0; break;
      case 'End': x = 1; break;
      case 'PageUp': x = clamp01(x + step * 5); break;
      case 'PageDown': x = clamp01(x - step * 5); break;
      default: handled = false;
    }
    if (!handled) return;
    ev.preventDefault();
    movedAt = performance.now();  /* a keypress is movement; dwell restarts, resolve ratchets */
    lastPointer = null;
    engage();
    announce();
  });

  root.addEventListener('focus', engage);

  /* ---- loop ------------------------------------------------------------- */

  function tick(t) {
    rafId = null;
    var dt = lastT ? Math.min(0.1, (t - lastT) / 1000) : 0.016;
    lastT = t;

    var wantPresence = engaged ? 1 : 0;
    presence += (wantPresence - presence) * Math.min(1, dt * 4.5);

    if (!done && engaged && visible) {
      /* Stillness is measured against the wall clock, not against frames. A
         visitor who holds for HOLD_SECONDS gets the resolve on a throttled tab
         or a slow phone exactly as they do at 60fps. Only the easing below is
         frame-driven, which is where a frame delta belongs. */
      var stillFor = (t - movedAt) / 1000;
      if (inBand() && stillFor > STILL_GRACE) {
        if (holdBase === null) holdBase = hold;
        hold = clamp01(holdBase + (stillFor - STILL_GRACE) / HOLD_SECONDS);
        if (hold * RATCHET > floorHold) floorHold = hold * RATCHET;
      } else {
        holdBase = null;
        hold = Math.max(floorHold, hold - dt * SCATTER_RATE);
      }
      if (hold >= 0.999) finish();
    }

    draw();

    if (!done && visible) { rafId = window.requestAnimationFrame(tick); }
    else { lastT = 0; }
  }

  function ensureLoop() {
    if (rafId !== null || done || !visible) return;
    rafId = window.requestAnimationFrame(tick);
  }

  function finish() {
    done = true;
    alreadyFound = true;
    persist();
    hold = 1;
    draw();
    root.classList.add('is-resolved');
    root.classList.remove('is-tuning');
    if (hintEl) hintEl.hidden = true;
    lineEl.removeAttribute('aria-hidden');
    root.setAttribute('aria-valuetext', 'Locked. ' + line);
    /* the canvas hands the line over to real text, then gets out of the way */
    window.setTimeout(function () {
      if (canvas.parentNode) canvas.parentNode.removeChild(canvas);
    }, 620);
  }

  /* ---- render ----------------------------------------------------------- */

  function draw() {
    resize();
    if (!W || !H) return;

    var p = proximity();
    var band = inBand();

    /* Seek is a coherence ramp, not a volume knob. Far out the field is coarse
       and genuinely random. As you close in, the random component gives way to
       a smooth concentric field, so the Bayer dither starts resolving it into
       ordered rings that rhyme with the circles already drawn on the panel.
       Hold then trades that field for the line. */
    var cell = Math.max(1, Math.round(8 - p * 3 - hold * 4));
    var density = 0.34 + p * 0.10 + hold * 0.08;

    ctx.clearRect(0, 0, W, H);
    ctx.fillStyle = INK;
    /* the field fades, the rail does not: the rail is the affordance and has to
       stay legible while the panel is still at rest */
    ctx.globalAlpha = clamp01(0.07 + presence * (0.13 + p * 0.46 + hold * 0.3));

    var gh = Math.ceil(H / cell);
    var gw = Math.ceil(W / cell);
    var lum = textLum;
    var h = hold;
    var ccx = W * 0.5, ccy = H * 0.5;
    var maxd = Math.sqrt(ccx * ccx + ccy * ccy) || 1;
    var RINGS = 5.5;

    for (var gy = 0; gy < gh; gy++) {
      var py = Math.min(H - 1, gy * cell + (cell >> 1));
      var rowOff = py * W;
      var bRow = (gy & 7) << 3;
      var dy = py - ccy;
      for (var gx = 0; gx < gw; gx++) {
        var px = Math.min(W - 1, gx * cell + (cell >> 1));
        var dx = px - ccx;
        var ring = 0.5 + 0.5 * Math.cos((Math.sqrt(dx * dx + dy * dy) / maxd) * RINGS * 6.2831853);
        var L = rnd() * (1 - p) + ring * p;      /* chaos gives way to order */
        if (lum && h > 0) {
          var o = (rowOff + px) << 2;
          L = (lum[o] / 255) * h + L * (1 - h);  /* order gives way to the line */
        }
        var threshold = (BAYER[bRow | (gx & 7)] + 0.5) / 64 * (density * 2);
        if (L < threshold) ctx.fillRect(gx * cell, gy * cell, cell, cell);
      }
    }

    ctx.globalAlpha = 1;
    drawRail(p, band);
  }

  /* the tuning rail: a thin scale along the bottom with a caret at x and a
     lock bracket that only draws once you are inside the band. */
  function drawRail(p, band) {
    var y = Math.round(H - Math.max(8, H * 0.055));
    var pad = Math.round(W * 0.06);
    var span = W - pad * 2;
    if (span < 20) return;

    ctx.fillStyle = INK;
    for (var i = 0; i <= 40; i++) {
      var tx = Math.round(pad + (span * i) / 40);
      var tall = i % 10 === 0;
      ctx.globalAlpha = tall ? 0.55 : 0.25;
      ctx.fillRect(tx, y - (tall ? 6 : 3), 1, tall ? 6 : 3);
    }

    if (band) {
      ctx.globalAlpha = 0.5 + 0.5 * hold;
      var b0 = Math.round(pad + span * clamp01(target - LOCK_BAND));
      var b1 = Math.round(pad + span * clamp01(target + LOCK_BAND));
      ctx.fillRect(b0, y - 11, 1, 11);
      ctx.fillRect(b1, y - 11, 1, 11);
      ctx.fillRect(b0, y - 11, b1 - b0, 1);
    }

    ctx.globalAlpha = 1;
    var cx = Math.round(pad + span * x);
    ctx.fillRect(cx - 1, y - 14, 3, 17);

    /* hold meter: the caret grows a stem as the line resolves */
    if (hold > 0) {
      var stem = Math.round(hold * Math.max(14, H * 0.06));
      ctx.fillRect(cx - 1, y + 4, 3, stem);
    }
    ctx.globalAlpha = 1;
  }

  /* ---- lifecycle -------------------------------------------------------- */

  if ('IntersectionObserver' in window) {
    var io = new IntersectionObserver(function (entries) {
      visible = entries[0].isIntersecting;
      if (visible) ensureLoop();
    }, { rootMargin: '10% 0px' });
    io.observe(root);
  } else {
    visible = true;
  }

  var resizeTimer = null;
  window.addEventListener('resize', function () {
    if (resizeTimer) clearTimeout(resizeTimer);
    resizeTimer = setTimeout(function () {
      W = 0; H = 0;
      applyViewport();
      resize();
      ensureLoop();
    }, 180);
  });

  resize();
  visible = true;   /* the observer corrects this on its first callback */
  draw();
  ensureLoop();
}());
